import React from "react";
import { observer } from "mobx-react";
import {
  Button,
  Checkbox,
  Flex,
  HStack,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { CanadaMap } from "@/components/CanadaMap";
import { renderInnerHtml } from "@/helpers/dom";
import { ScrollToElement } from "@/components/ScrollToElement";
import type { OrderCheckboxData } from "@/store/types/order";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { AppLink } from "@/routing/AppLink";
import { OrderUnitDetails } from "./UnitDetails";
import { H3 } from "@/components/Heading";
import { ExtraUnitsCallout } from "@/components/ExtraUnitsCallout";

export const OrderDetailsBasePanel = observer(() => {
  const {
    activeLanguage,
    activeProvinceId,
    isIndigenousFlow,
    setOrderData,
    isValidOrderMinimum,
    activeProvinceTranslated,
    orderData,
    orderCheckboxData,
    toggleOrderCheckbox,
    getProvincePrefix,
    isEmergentFlow,
    ga4Store,
    telemetryStore,
  } = useStore();
  const language = translations.pages["order-details"][activeLanguage];
  const languageOntarioDetails =
    translations.pages["order-details-ontario"][activeLanguage];

  const navigate = useAppNavigate();

  const handleCheckboxChange = (key: keyof OrderCheckboxData) =>
    toggleOrderCheckbox(key);

  const handleCheckboxEmailUpdates = (value: boolean) => {
    setOrderData({
      ...orderData,
      subscribeToEmailUpdates: value,
    });
  };

  const handleProceedNext = () => {
    if (!isValid) {
      telemetryStore.detailsContinueBlocked(requiredCheckboxes);
      return;
    }

    telemetryStore.detailsSubmitted();
    ga4Store.addToCart();

    navigate({
      to: isIndigenousFlow ? "/order/summary" : "/order/shipping",
    });
  };

  const isIndigenousCheckboxValid = isIndigenousFlow
    ? orderCheckboxData.agreeToShareMyData && orderCheckboxData.agreeToFairUse
    : true;

  const hasExtraUnitCostsCallout =
    !isIndigenousFlow && location.pathname.includes("details");

  const isValid =
    (!isEmergentFlow
      ? orderCheckboxData.agreeToTerms
      : !orderCheckboxData.agreeToTerms) &&
    orderCheckboxData.agreeToPrivacyPolicy &&
    orderCheckboxData.confirmAge &&
    isIndigenousCheckboxValid &&
    isValidOrderMinimum;

  const requiredCheckboxes: (keyof OrderCheckboxData)[] = [
    "agreeToPrivacyPolicy",
    "confirmAge",
  ];

  if (!isEmergentFlow) requiredCheckboxes.push("agreeToTerms");
  if (isIndigenousFlow)
    requiredCheckboxes.push("agreeToShareMyData", "agreeToFairUse");

  return (
    <Stack spacing="14">
      <ScrollToElement />

      {!isEmergentFlow && !isIndigenousFlow && (
        <>
          <CanadaMap svgProps={{ height: "350" }} />
          <Flex justifyContent="center">
            <H3
              maxW="container.md"
              textAlign="center"
            >
              {renderInnerHtml(language.residents)}
              {getProvincePrefix(activeProvinceId!)}
              <Text
                as="span"
                color="primary-light"
              >
                {activeProvinceTranslated}
              </Text>
              <Text as="span">{renderInnerHtml(language.are_eligible)}</Text>
            </H3>
          </Flex>
        </>
      )}

      {hasExtraUnitCostsCallout && <ExtraUnitsCallout />}

      <Stack spacing="8">
        <H3>{language.enter_order_details}</H3>
        <OrderUnitDetails />
      </Stack>

      <Stack spacing="4">
        <Text fontWeight="bold">{language.required}</Text>
        <Stack>
          <Checkbox
            id="checkbox1"
            isChecked={orderCheckboxData.agreeToPrivacyPolicy}
            onChange={() => handleCheckboxChange("agreeToPrivacyPolicy")}
          >
            {renderInnerHtml(language.i_agree)}
            <Link
              textDecoration="underline"
              href="https://www.emergentbiosolutions.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {renderInnerHtml(language.privacy_policy)}
            </Link>
            .
          </Checkbox>

          {!isEmergentFlow && (
            <Checkbox
              id="checkbox2"
              isChecked={orderCheckboxData.agreeToTerms}
              onChange={() => handleCheckboxChange("agreeToTerms")}
            >
              {renderInnerHtml(language.i_acknowledge)}
              <Link
                as={AppLink}
                to="/general-terms"
                target="_blank"
                rel="noopener noreferrer"
                textDecoration="underline"
              >
                {language.general_terms}
              </Link>
              .
            </Checkbox>
          )}

          <Checkbox
            id="checkbox3"
            isChecked={orderCheckboxData.confirmAge}
            onChange={() => handleCheckboxChange("confirmAge")}
          >
            {renderInnerHtml(language.i_confirm)}
          </Checkbox>

          {isIndigenousFlow && (
            <>
              <Checkbox
                id="checkbox5"
                isChecked={orderCheckboxData.agreeToShareMyData}
                onChange={() => handleCheckboxChange("agreeToShareMyData")}
              >
                {renderInnerHtml(
                  languageOntarioDetails.i_acknowledge_ordering_process,
                )}
              </Checkbox>

              <Checkbox
                id="checkbox6"
                isChecked={orderCheckboxData.agreeToFairUse}
                onChange={() => handleCheckboxChange("agreeToFairUse")}
              >
                {renderInnerHtml(language.i_affirm_community)}
              </Checkbox>
            </>
          )}
        </Stack>
      </Stack>

      {!isIndigenousFlow && (
        <Stack spacing="4">
          <Text fontWeight="bold">{language.optional}</Text>
          <Checkbox
            id="checkbox4"
            isChecked={orderData.subscribeToEmailUpdates}
            onChange={() =>
              handleCheckboxEmailUpdates(!orderData.subscribeToEmailUpdates)
            }
          >
            {renderInnerHtml(language.i_like_emails)}
          </Checkbox>
        </Stack>
      )}

      {!isIndigenousFlow && <Text>{renderInnerHtml(language.order_note)}</Text>}

      <HStack justifyContent="flex-end">
        <Button
          onClick={handleProceedNext}
          aria-disabled={!isValid}
        >
          {isIndigenousFlow
            ? language.proceed_to_review
            : language.proceed_to_billing}
        </Button>
      </HStack>
    </Stack>
  );
});
